// frontend/src/pages/staff/StaffServiceAdd.js
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import StaffSidebarLayout from "./StaffSidebarLayout";
import axios from "axios";
import "./staffStyles.css";
import PriceTiersEditor from "../../components/PriceTiersEditor";

// Utility function to convert text to user-friendly Title Case
const toUserFriendly = (text) => {
  if (!text) return "";
  // Convert kebab-case, snake_case, and camelCase to spaces
  return text
    .replace(/[-_]/g, " ") // Replace hyphens and underscores with spaces
    .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space before capital letters (camelCase)
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

function StaffServiceAdd() {
  const navigate = useNavigate();
  const location = useLocation();
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    durationMinutes: "",
    pricingType: "fixed",
    hourlyRate: "",
    maxHoursPerBooking: 8,
    priceTiers: [],
    category: "other",
    serviceImage: "",
    applicableTo: [], // Which professional types this service applies to
    platformTemplateId: null, // Track platform template usage
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [staffProfessionalTypes, setStaffProfessionalTypes] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  // Platform Templates State
  const [showTemplates, setShowTemplates] = useState(false);
  const [platformTemplates, setPlatformTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templatesError, setTemplatesError] = useState("");
  const [templateSearchTerm, setTemplateSearchTerm] = useState("");
  const [templateCategoryFilter, setTemplateCategoryFilter] = useState("all");
  const [selectedTemplates, setSelectedTemplates] = useState([]); // For multiple selection

  // Custom Confirm Modal State
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState({
    message: "",
    onConfirm: null,
    onCancel: null,
  });

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Determine role type from path
  const getRoleType = useCallback(() => {
    return "professional";
  }, []);

  const getBasePath = useCallback(() => {
    const roleType = getRoleType();
    return `/dashboard/${roleType}`;
  }, [getRoleType]);

  // Security: Get auth headers
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  // Security: Validate API base URL
  const getApiBaseUrl = useCallback(() => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL) {
      setErrorMsg("Configuration error. Please contact support.");
      return null;
    }
    return baseURL;
  }, []);

  // Fetch staff profile to get professional types
  useEffect(() => {
    const fetchStaffProfile = async () => {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      try {
        const response = await axios.get(`${baseURL}/api/staff/me`, {
          headers,
        });
        const staff = response.data.staff;
        const types = staff?.professionalTypes || [];
        setStaffProfessionalTypes(types);

        // Fetch business info to get business types
        if (staff.businessId) {
          const businessResponse = await axios.get(
            `${baseURL}/api/business/${staff.businessId}`,
            { headers },
          );
          const businessTypes = businessResponse.data?.type || [];

          // Fetch service categories based on business types (e.g., ['hair-studio', 'salon'])
          if (businessTypes && businessTypes.length > 0) {
            const businessCategoriesParam = businessTypes.join(",");
            const categoriesResponse = await axios.get(
              `${baseURL}/api/platform-settings/service-categories?businessCategories=${businessCategoriesParam}`,
            );
            const fetchedCategories = categoriesResponse.data.categories || [];
            if (fetchedCategories.length > 0) {
              setServiceCategories(fetchedCategories);
            }
          }
        }
      } catch (err) {
        // Fallback to default categories if API fails
        setServiceCategories([
          { value: "haircut", label: "Haircut" },
          { value: "color", label: "Color" },
          { value: "styling", label: "Styling" },
          { value: "treatment", label: "Treatment" },
          { value: "spa", label: "Spa" },
          { value: "nails", label: "Nails" },
          { value: "makeup", label: "Makeup" },
          { value: "waxing", label: "Waxing" },
          { value: "other", label: "Other" },
        ]);
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchStaffProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount - getAuthHeaders and getApiBaseUrl are stable

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear messages on input change
    setErrorMsg("");
    setSuccessMsg("");
  };

  const handleApplicableToToggle = (type) => {
    setFormData((prev) => {
      const isSelected = prev.applicableTo.includes(type);
      const newApplicableTo = isSelected
        ? prev.applicableTo.filter((t) => t !== type)
        : [...prev.applicableTo, type].slice(0, 3); // Max 3 types
      return { ...prev, applicableTo: newApplicableTo };
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        setErrorMsg("Please select an image file");
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrorMsg("Image must be less than 5MB");
        return;
      }

      setImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setErrorMsg("");
    }
  };

  // Fetch Platform Templates
  const fetchPlatformTemplates = async () => {
    setLoadingTemplates(true);
    setTemplatesError("");

    try {
      const baseURL = getApiBaseUrl();
      if (!baseURL) return;

      // Get business types from staff profile
      const headers = getAuthHeaders();
      if (!headers) return;

      const staffResponse = await axios.get(`${baseURL}/api/staff/me`, {
        headers,
      });
      const staff = staffResponse.data.staff;

      // Get business types from business
      let businessTypes = [];
      if (staff.businessId) {
        try {
          const businessResponse = await axios.get(
            `${baseURL}/api/business/${staff.businessId}`,
            { headers },
          );
          businessTypes = businessResponse.data?.type || [];
        } catch (err) {
        }
      }

      // Fetch templates filtered by business types
      const params = new URLSearchParams();
      if (businessTypes.length > 0) {
        params.append("businessTypes", businessTypes.join(","));
      }
      params.append("limit", "100");

      const response = await axios.get(
        `${baseURL}/api/platform-settings/service-templates/public?${params.toString()}`,
      );

      setPlatformTemplates(response.data.templates || []);
    } catch (error) {
      setTemplatesError("Failed to load templates. Please try again.");
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Handle template selection (single - pre-fill form)
  const handleUsePlatformTemplate = (template) => {
    // Pre-fill form with template values
    setFormData({
      name: template.name,
      description: template.description || "",
      price: template.suggestedPrice || "",
      durationMinutes: template.suggestedDuration || "",
      category: template.category,
      serviceImage: "",
      applicableTo: template.applicableTo || [],
      platformTemplateId: template._id, // Track for analytics
    });

    // Close templates browser
    setShowTemplates(false);

    // Scroll to form
    window.scrollTo({ top: 0, behavior: "smooth" });

    // Show success message
    setSuccessMsg(
      `Template "${template.name}" loaded! You can customize the details below.`,
    );
    setTimeout(() => setSuccessMsg(""), 5000);
  };

  // Toggle template selection (multi-select)
  const handleToggleTemplateSelection = (templateId) => {
    setSelectedTemplates((prev) => {
      if (prev.includes(templateId)) {
        return prev.filter((id) => id !== templateId);
      } else {
        return [...prev, templateId];
      }
    });
  };

  // Bulk add selected templates
  const handleBulkAddTemplates = async () => {
    if (selectedTemplates.length === 0) {
      setErrorMsg("Please select at least one template");
      setTimeout(() => setErrorMsg(""), 5000);
      return;
    }

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      // Fetch existing services for this staff member to check for duplicates
      const existingServicesResponse = await axios.get(
        `${baseURL}/api/staff/me/services`,
        { headers },
      );
      const existingServicesData = existingServicesResponse.data || {};
      const existingServices = [
        ...(existingServicesData.templateServices || []),
        ...(existingServicesData.professionalServices || []),
      ];
      const existingNames = existingServices.map((s) =>
        s.name.toLowerCase().trim(),
      );

      // Get selected template objects
      const templatesToAdd = platformTemplates.filter((t) =>
        selectedTemplates.includes(t._id),
      );

      // Check for duplicates
      const duplicates = [];
      const uniqueTemplates = [];

      for (const template of templatesToAdd) {
        const templateName = template.name.toLowerCase().trim();
        if (existingNames.includes(templateName)) {
          duplicates.push(template.name);
        } else {
          uniqueTemplates.push(template);
        }
      }

      // Show warning if duplicates found
      if (duplicates.length > 0) {
        await new Promise((resolve) => {
          setConfirmModalData({
            message: (
              <div>
                <p style={{ marginBottom: "12px", fontWeight: "600" }}>
                  The following service(s) already exist and will be skipped:
                </p>
                <ul
                  style={{
                    marginLeft: "20px",
                    marginBottom: "16px",
                    color: "#dc2626",
                  }}
                >
                  {duplicates.map((name, i) => (
                    <li key={i}>{name}</li>
                  ))}
                </ul>
                <p style={{ fontWeight: "600", color: "#059669" }}>
                  {uniqueTemplates.length} new service(s) will be added.
                  Continue?
                </p>
              </div>
            ),
            onConfirm: () => {
              setShowConfirmModal(false);
              resolve(true);
            },
            onCancel: () => {
              setShowConfirmModal(false);
              resolve(false);
            },
          });
          setShowConfirmModal(true);
        }).then((shouldContinue) => {
          if (!shouldContinue) return;
          continueAddingServices();
        });
        return;
      }

      continueAddingServices();

      async function continueAddingServices() {
        if (uniqueTemplates.length === 0) {
          setErrorMsg(
            "All selected services already exist. No duplicates created.",
          );
          setTimeout(() => setErrorMsg(""), 7000);
          setSelectedTemplates([]);
          return;
        }

        // Create all unique services
        const createdServices = [];
        for (const template of uniqueTemplates) {
          const submitData = {
            name: template.name,
            description: template.description || "",
            price: template.suggestedPrice || "",
            durationMinutes: template.suggestedDuration || 30,
            category: template.category,
            applicableTo: template.applicableTo || [],
            platformTemplateId: template._id,
          };

          const response = await axios.post(
            `${baseURL}/api/staff/services`,
            submitData,
            { headers },
          );
          createdServices.push(response.data.service);
        }

        // Success!
        const message =
          duplicates.length > 0
            ? `✓ ${createdServices.length} service${createdServices.length > 1 ? "s" : ""} added! (${duplicates.length} duplicate${duplicates.length > 1 ? "s" : ""} skipped)`
            : `✓ ${createdServices.length} service${createdServices.length > 1 ? "s" : ""} added successfully! You can find and edit them in your services list.`;

        setSuccessMsg(message);
        setTimeout(() => setSuccessMsg(""), 7000);

        // Clear selection and close
        setSelectedTemplates([]);
        setShowTemplates(false);

        // Scroll to top
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (error) {
      setErrorMsg("Failed to add services. Please try again.");
      setTimeout(() => setErrorMsg(""), 5000);
    }
  };

  // Toggle templates browser
  const handleToggleTemplates = () => {
    if (!showTemplates && platformTemplates.length === 0) {
      fetchPlatformTemplates();
    }
    setShowTemplates(!showTemplates);
  };

  // Filter templates
  const filteredTemplates = platformTemplates.filter((template) => {
    if (
      templateCategoryFilter !== "all" &&
      template.category !== templateCategoryFilter
    ) {
      return false;
    }

    if (templateSearchTerm) {
      const search = templateSearchTerm.toLowerCase();
      return (
        template.name.toLowerCase().includes(search) ||
        (template.description &&
          template.description.toLowerCase().includes(search))
      );
    }

    return true;
  });

  // Image upload function - COMMENTED OUT PER USER REQUEST
  /*
  const uploadImage = async () => {
    if (!imageFile) return null;

    setUploadingImage(true);
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return null;

      const formData = new FormData();
      formData.append('image', imageFile);

      const res = await axios.post(
        `${baseURL}/api/staff/services/upload-image`,
        formData,
        {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      return res.data.imageUrl;
    } catch (err) {
      throw new Error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };
  */

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");
    setSaving(true);

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) {
        setSaving(false);
        return;
      }

      // Validate inputs
      if (!formData.name.trim()) {
        setErrorMsg("Service name is required");
        setSaving(false);
        return;
      }

      if (
        formData.pricingType !== "range" &&
        formData.pricingType !== "hourly" &&
        (!formData.price || parseFloat(formData.price) < 0)
      ) {
        setErrorMsg("Valid price is required");
        setSaving(false);
        return;
      }

      if (
        formData.pricingType !== "hourly" &&
        formData.pricingType !== "range" &&
        (!formData.durationMinutes || parseInt(formData.durationMinutes) < 5)
      ) {
        setErrorMsg("Duration must be at least 5 minutes");
        setSaving(false);
        return;
      }

      // Upload image if provided - COMMENTED OUT PER USER REQUEST
      /*
      let uploadedImageUrl = null;
      if (imageFile) {
        try {
          uploadedImageUrl = await uploadImage();
        } catch (err) {
          setErrorMsg('Failed to upload image. Please try again.');
          setSaving(false);
          return;
        }
      }
      */

      // Send create request
      const serviceData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        price: parseFloat(formData.price),
        durationMinutes: parseInt(formData.durationMinutes),
        category: formData.category,
        // serviceImage: uploadedImageUrl, // COMMENTED OUT - no image upload
        applicableTo: formData.applicableTo, // Empty array = applies to all types
        platformTemplateId: formData.platformTemplateId, // Track template usage
        pricingType: formData.pricingType || "fixed",
        // F1: per-unit - base price is the per-unit price, duration is per unit
        ...(formData.pricingType === "per_unit"
          ? {
              pricePerUnit: parseFloat(formData.price),
              maxUnitsPerBooking: 10,
            }
          : {}),
      };

      // F2: hourly = rate x hours; range = customer picks one of the options
      if (formData.pricingType === "hourly") {
        const rate = parseFloat(formData.hourlyRate);
        if (Number.isFinite(rate)) {
          serviceData.hourlyRate = rate;
          serviceData.price = rate;
        }
        serviceData.maxHoursPerBooking = parseInt(
          formData.maxHoursPerBooking || 8,
          10,
        );
        // Duration field is hidden for hourly - the engine bills 60 min per hour.
        serviceData.durationMinutes = 60;
      }
      if (formData.pricingType === "range") {
        const usableTiers = (formData.priceTiers || [])
          .map((tier) => ({
            label: String(tier.label || "").trim(),
            price: parseFloat(tier.price),
            durationMinutes: parseInt(tier.durationMinutes, 10),
          }))
          .filter((tier) => tier.label && Number.isFinite(tier.price));

        if (usableTiers.length < 2) {
          setErrorMsg(
            "Add at least 2 priced options (for example: Shoulder length $150, Mid-back $220).",
          );
          return;
        }

        const missingMinutes = usableTiers.some(
          (tier) =>
            !Number.isFinite(tier.durationMinutes) || tier.durationMinutes < 5,
        );
        if (missingMinutes) {
          setErrorMsg("Give every option a duration of at least 5 minutes.");
          return;
        }

        const tierPrices = usableTiers.map((t) => t.price);
        serviceData.priceTiers = usableTiers;
        serviceData.price = Math.min(...tierPrices);
        serviceData.maxPrice = Math.max(...tierPrices);
        // Duration field is hidden for range - use the longest option length.
        serviceData.durationMinutes = Math.max(
          ...usableTiers.map((t) => Number(t.durationMinutes) || 0),
        );
      }

      await axios.post(`${baseURL}/api/staff/services`, serviceData, {
        headers,
      });

      setSuccessMsg("Service created successfully!");

      // Redirect back to services list after 1.5 seconds
      setTimeout(() => {
        const basePath = getBasePath();
        navigate(`${basePath}/services`);
      }, 1500);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      } else {
        const errorMessage =
          err?.response?.data?.message ||
          err.message ||
          "Unable to create service. Please try again.";
        setErrorMsg(errorMessage);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    const basePath = getBasePath();
    navigate(`${basePath}/services`);
  };

  const roleType = getRoleType();
  const basePath = getBasePath();

  return (
    <StaffSidebarLayout roleType={roleType}>
      <div
        style={{
          maxWidth: "700px",
          margin: "0 auto",
          padding: "20px",
          paddingBottom: window.innerWidth < 768 ? "180px" : "60px",
        }}
      >
        {/* Back Button */}
        <button
          onClick={() => navigate(`${basePath}/services`)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 16px",
            marginBottom: "20px",
            border: "1px solid #ddd",
            borderRadius: "8px",
            background: "#fff",
            color: "#333",
            fontSize: "14px",
            fontWeight: "500",
            cursor: "pointer",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.target.style.background = "#f5f5f5";
            e.target.style.borderColor = "#001d4a";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "#fff";
            e.target.style.borderColor = "#ddd";
          }}
        >
          <span>←</span> Back to Services
        </button>

        {/* Header */}
        <div style={{ marginBottom: "30px" }}>
          <h2 style={{ margin: "0 0 8px 0" }}>Add Service</h2>
          <p style={{ margin: 0, color: "#666" }}>
            Create a new service offering
          </p>
          {/* Phase 5: Location context info */}
          <div
            style={{
              marginTop: "12px",
              padding: "12px",
              background: "#f0f9ff",
              border: "1px solid #bae6fd",
              borderRadius: "8px",
              display: "flex",
              alignItems: "start",
              gap: "8px",
            }}
          >
            <span style={{ fontSize: "16px", flexShrink: 0 }}>📍</span>
            <p
              style={{
                margin: 0,
                fontSize: "13px",
                color: "#0369a1",
                lineHeight: "1.5",
              }}
            >
              This service will be added to your current active location. To
              offer at multiple locations, you'll be able to manage pricing per
              location in the future.
            </p>
          </div>
        </div>

        {/* Error Message */}
        {errorMsg && (
          <div
            style={{
              background: "#fff3f3",
              border: "1px solid #c00",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "20px",
            }}
          >
            <p style={{ margin: 0, color: "#c00" }}>{errorMsg}</p>
          </div>
        )}

        {/* Success Message */}
        {successMsg && (
          <div
            style={{
              background: "#e6ffed",
              border: "1px solid #03543f",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "20px",
            }}
          >
            <p style={{ margin: 0, color: "#03543f" }}>{successMsg}</p>
          </div>
        )}

        {/* Platform Templates Browser Button */}
        <div style={{ marginBottom: "20px" }}>
          <div
            style={{
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: "10px",
              padding: "16px",
              marginBottom: "12px",
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                color: "#6b7280",
                lineHeight: "1.5",
              }}
            >
              💡 <strong style={{ color: "#374151" }}>Tip:</strong> Check with
              your business first if they have service templates available. If
              not, browse our platform library below for quick-start options.
            </p>
          </div>
          <button
            type="button"
            onClick={handleToggleTemplates}
            className="btn-secondary"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              padding: "12px 20px",
              background: "#0b63ce",
              color: "#ffffff",
              border: "1px solid #0a56b3",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "700",
              cursor: "pointer",
              transition: "all 0.2s",
              width: "100%",
              boxShadow: "0 4px 14px rgba(11, 99, 206, 0.35)",
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = "#084a9a";
              e.target.style.background = "#0957b8";
              e.target.style.boxShadow = "0 6px 18px rgba(11, 99, 206, 0.45)";
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = "#0a56b3";
              e.target.style.background = "#0b63ce";
              e.target.style.boxShadow = "0 4px 14px rgba(11, 99, 206, 0.35)";
            }}
          >
            <span style={{ fontSize: "18px" }}>📋</span>
            {showTemplates
              ? "Hide Platform Templates"
              : "Browse Platform Templates"}
          </button>
        </div>

        {/* Platform Templates Browser */}
        {showTemplates && (
          <div
            className="dashboard-card"
            style={{ padding: "20px", marginBottom: "20px" }}
          >
            <h3
              style={{
                marginTop: 0,
                marginBottom: "8px",
                color: "#001d4a",
                fontSize: "18px",
                fontWeight: "600",
              }}
            >
              📚 Platform Service Templates
            </h3>
            <p
              style={{
                color: "#6b7280",
                fontSize: "14px",
                marginBottom: "16px",
                lineHeight: "1.5",
              }}
            >
              <strong>Two ways to use templates:</strong>
              <br />• <strong>Select multiple</strong> (checkboxes) and add them
              all at once as ready-to-use services
              <br />• <strong>Click a card</strong> to pre-fill the form below
              for customization before saving
            </p>

            {/* Search and Filter */}
            <div
              style={{
                display: "flex",
                gap: "12px",
                marginBottom: "16px",
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              <input
                type="text"
                placeholder="Search templates..."
                value={templateSearchTerm}
                onChange={(e) => setTemplateSearchTerm(e.target.value)}
                style={{
                  flex: isMobile ? "none" : 1,
                  padding: "8px 12px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              />
              <select
                value={templateCategoryFilter}
                onChange={(e) => setTemplateCategoryFilter(e.target.value)}
                style={{
                  flex: isMobile ? "none" : "0 0 200px",
                  padding: "8px 12px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              >
                <option value="all">All Categories</option>
                {serviceCategories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Templates List */}
            {loadingTemplates ? (
              <div
                style={{ textAlign: "center", padding: "40px", color: "#666" }}
              >
                Loading templates...
              </div>
            ) : templatesError ? (
              <div
                style={{
                  background: "#fff3f3",
                  border: "1px solid #c00",
                  borderRadius: "6px",
                  padding: "12px",
                  color: "#c00",
                }}
              >
                {templatesError}
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div
                style={{ textAlign: "center", padding: "40px", color: "#666" }}
              >
                No templates found
              </div>
            ) : (
              <>
                {/* Bulk Add Button */}
                {selectedTemplates.length > 0 && (
                  <div style={{ marginBottom: "16px", textAlign: "center" }}>
                    <button
                      onClick={handleBulkAddTemplates}
                      style={{
                        padding: "12px 24px",
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        color: "#fff",
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "15px",
                        fontWeight: "600",
                        cursor: "pointer",
                        boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = "translateY(-2px)";
                        e.target.style.boxShadow =
                          "0 6px 16px rgba(102, 126, 234, 0.5)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow =
                          "0 4px 12px rgba(102, 126, 234, 0.4)";
                      }}
                    >
                      ✓ Add {selectedTemplates.length} Selected Service
                      {selectedTemplates.length > 1 ? "s" : ""}
                    </button>
                    <button
                      onClick={() => setSelectedTemplates([])}
                      style={{
                        marginLeft: "12px",
                        padding: "12px 24px",
                        background: "#fff",
                        color: "#6b7280",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontWeight: "500",
                        cursor: "pointer",
                      }}
                    >
                      Clear Selection
                    </button>
                  </div>
                )}

                {/* Templates Grid */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr"
                      : "repeat(auto-fill, minmax(280px, 1fr))",
                    gap: "12px",
                    maxHeight: "500px",
                    overflowY: "auto",
                  }}
                >
                  {filteredTemplates.map((template) => {
                    const isSelected = selectedTemplates.includes(template._id);
                    return (
                      <div
                        key={template._id}
                        style={{
                          padding: "16px",
                          border: isSelected
                            ? "2px solid #667eea"
                            : "1px solid #ddd",
                          borderRadius: "8px",
                          background: isSelected ? "#f0f9ff" : "#fff",
                          position: "relative",
                          transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.borderColor = "#c7d2fe";
                            e.currentTarget.style.boxShadow =
                              "0 2px 8px rgba(102, 126, 234, 0.15)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.borderColor = "#ddd";
                            e.currentTarget.style.boxShadow = "none";
                          }
                        }}
                      >
                        {/* Checkbox for multi-select */}
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleTemplateSelection(template._id);
                          }}
                          style={{
                            position: "absolute",
                            top: "12px",
                            right: "12px",
                            width: "24px",
                            height: "24px",
                            background: isSelected ? "#667eea" : "#fff",
                            border: isSelected ? "none" : "2px solid #d1d5db",
                            borderRadius: "6px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                            color: "#fff",
                            fontWeight: "bold",
                            cursor: "pointer",
                            transition: "all 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.borderColor = "#667eea";
                              e.currentTarget.style.background = "#f0f9ff";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.borderColor = "#d1d5db";
                              e.currentTarget.style.background = "#fff";
                            }
                          }}
                        >
                          {isSelected && "✓"}
                        </div>

                        {/* Card content - click to pre-fill form */}
                        <div
                          onClick={() => handleUsePlatformTemplate(template)}
                          style={{ cursor: "pointer", paddingRight: "32px" }}
                        >
                          <h4
                            style={{
                              margin: "0 0 8px 0",
                              color: "#001d4a",
                              fontSize: "16px",
                            }}
                          >
                            {template.name}
                          </h4>
                          {template.description && (
                            <p
                              style={{
                                margin: "0 0 12px 0",
                                color: "#666",
                                fontSize: "13px",
                                lineHeight: "1.4",
                              }}
                            >
                              {template.description}
                            </p>
                          )}
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              flexWrap: "wrap",
                              fontSize: "12px",
                            }}
                          >
                            {template.suggestedPrice && (
                              <span
                                style={{
                                  padding: "4px 8px",
                                  background: "#e8f5e9",
                                  color: "#2e7d32",
                                  borderRadius: "4px",
                                  fontWeight: "500",
                                }}
                              >
                                ${template.suggestedPrice}
                              </span>
                            )}
                            {template.suggestedDuration && (
                              <span
                                style={{
                                  padding: "4px 8px",
                                  background: "#e3f2fd",
                                  color: "#1565c0",
                                  borderRadius: "4px",
                                }}
                              >
                                {template.suggestedDuration} min
                              </span>
                            )}
                            <span
                              style={{
                                padding: "4px 8px",
                                background: "#f3e5f5",
                                color: "#6a1b9a",
                                borderRadius: "4px",
                              }}
                            >
                              {toUserFriendly(template.category)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* Form */}
        <div className="dashboard-card" style={{ padding: "30px" }}>
          <form onSubmit={handleSubmit}>
            {/* Service Name */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                  color: "#001d4a",
                }}
              >
                Service Name <span style={{ color: "#c00" }}>*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                maxLength={100}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                }}
                placeholder="e.g., Haircut & Style"
              />
            </div>

            {/* Description */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                  color: "#001d4a",
                }}
              >
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                maxLength={500}
                rows={4}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  fontFamily: "inherit",
                  resize: "vertical",
                }}
                placeholder="Describe what this service includes..."
              />
              <p
                style={{ margin: "5px 0 0 0", fontSize: "12px", color: "#666" }}
              >
                {formData.description.length}/500 characters
              </p>
            </div>

            {/* Pricing Type (F1) */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                  color: "#001d4a",
                }}
              >
                Pricing Type
              </label>
              <select
                name="pricingType"
                value={formData.pricingType}
                onChange={handleInputChange}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "16px",
                  background: "#fff",
                  cursor: "pointer",
                  boxSizing: "border-box",
                }}
              >
                <option value="fixed">Fixed Price</option>
                <option value="per_unit">Per Unit (e.g. per nail)</option>
                <option value="hourly">Hourly (rate x hours)</option>
                <option value="range">Options (customer picks one)</option>
                {/* From / Variable hidden until properly wired */}
              </select>
              <p
                style={{ margin: "5px 0 0 0", fontSize: "12px", color: "#666" }}
              >
                {formData.pricingType === "fixed" &&
                  "Exact price for the service"}
                {formData.pricingType === "hourly" &&
                  "Charged per hour of work"}
                {formData.pricingType === "range" &&
                  "Customers pick one priced option"}
                {formData.pricingType === "per_unit" &&
                  "Per unit pricing: enter the per-unit price below and the time per unit as the Duration (e.g. 15 min per nail). Customers choose the quantity (max 10)."}
              </p>
            </div>

            {/* Hourly Rate - Only for hourly pricing */}
            {formData.pricingType === "hourly" && (
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#001d4a",
                  }}
                >
                  Hourly Rate (USD) <span style={{ color: "#c00" }}>*</span>
                </label>
                <input
                  type="number"
                  name="hourlyRate"
                  value={formData.hourlyRate}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="0.01"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                  }}
                  placeholder="Rate per hour"
                />
                <p
                  style={{ margin: "5px 0 0 0", fontSize: "12px", color: "#666" }}
                >
                  The appointment length is the number of hours the customer
                  books (hours x 60 minutes).
                </p>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginTop: "16px",
                    marginBottom: "8px",
                    color: "#001d4a",
                  }}
                >
                  Max hours per booking
                </label>
                <input
                  type="number"
                  name="maxHoursPerBooking"
                  value={formData.maxHoursPerBooking}
                  onChange={handleInputChange}
                  min="1"
                  max="24"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                  }}
                />
                <p
                  style={{ margin: "5px 0 0 0", fontSize: "12px", color: "#666" }}
                >
                  Customers pick how many hours they need. Total price = hourly
                  rate x hours, and the appointment length scales the same way.
                </p>
              </div>
            )}

            {/* Price options - Only for range pricing (customer picks one) */}
            {formData.pricingType === "range" && (
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#001d4a",
                  }}
                >
                  Price options
                </label>
                <PriceTiersEditor
                  tiers={formData.priceTiers || []}
                  onChange={(nextTiers) =>
                    setFormData((prev) => ({ ...prev, priceTiers: nextTiers }))
                  }
                />
              </div>
            )}

            {/* Price and Duration */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "20px",
                marginBottom: "20px",
              }}
            >
              {formData.pricingType !== "range" &&
                formData.pricingType !== "hourly" && (
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#001d4a",
                  }}
                >
                  {formData.pricingType === "per_unit"
                    ? "Price per unit (USD)"
                    : "Price (USD)"}{" "}
                  <span style={{ color: "#c00" }}>*</span>
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="0.01"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                  }}
                  placeholder="0.00"
                />
              </div>
              )}

              {formData.pricingType !== "hourly" &&
                formData.pricingType !== "range" && (
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#001d4a",
                  }}
                >
                  {formData.pricingType === "per_unit"
                    ? "Minutes per unit"
                    : "Duration (minutes)"}{" "}
                  <span style={{ color: "#c00" }}>*</span>
                </label>
                <input
                  type="number"
                  name="durationMinutes"
                  value={formData.durationMinutes}
                  onChange={handleInputChange}
                  required
                  min="5"
                  step="5"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                  }}
                  placeholder="30"
                />
              </div>
              )}
            </div>

            {/* Category */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                  color: "#001d4a",
                }}
              >
                Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "16px",
                  background: "#fff",
                  cursor: "pointer",
                  boxSizing: "border-box",
                }}
              >
                {loadingCategories ? (
                  <option disabled>Loading categories...</option>
                ) : (
                  serviceCategories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))
                )}
              </select>
            </div>

            {/* Applicable To (Professional Types) */}
            {staffProfessionalTypes.length > 1 && (
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#001d4a",
                  }}
                >
                  Service Applies To
                </label>
                <p
                  style={{
                    fontSize: "12px",
                    color: "#666",
                    marginBottom: "10px",
                  }}
                >
                  Leave empty if this service applies to all your professional
                  types. Otherwise, select which types (max 3).
                </p>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "12px",
                  }}
                >
                  {staffProfessionalTypes.map((type) => {
                    // type is a string, not an object
                    return (
                      <label
                        key={type}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "10px 12px",
                          border: "2px solid",
                          borderColor: formData.applicableTo.includes(type)
                            ? "#001d4a"
                            : "#ddd",
                          borderRadius: "8px",
                          cursor: "pointer",
                          background: formData.applicableTo.includes(type)
                            ? "#e8f4f8"
                            : "white",
                          transition: "all 0.2s",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.applicableTo.includes(type)}
                          onChange={() => handleApplicableToToggle(type)}
                          disabled={
                            formData.applicableTo.length >= 3 &&
                            !formData.applicableTo.includes(type)
                          }
                          style={{ marginRight: "8px" }}
                        />
                        <span style={{ fontSize: "14px", fontWeight: "500" }}>
                          {toUserFriendly(type)}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <p
                  style={{
                    margin: "5px 0 0 0",
                    fontSize: "12px",
                    color: "#666",
                  }}
                >
                  {formData.applicableTo.length === 0
                    ? "Applies to all your professional types"
                    : `Selected ${formData.applicableTo.length}/3 types`}
                </p>
              </div>
            )}

            {/* Service Image Upload - COMMENTED OUT PER USER REQUEST
            <div style={{ marginBottom: '30px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                marginBottom: '8px',
                color: '#001d4a'
              }}>
                Service Image
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px',
                  boxSizing: 'border-box',
                  cursor: 'pointer'
                }}
              />
              <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#666' }}>
                Optional: Upload an image showcasing this service (max 5MB)
              </p>

              {/* Image Preview */}
            {/* {imagePreview && (
                <div style={{ marginTop: '15px', textAlign: 'center' }}>
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    style={{ 
                      maxWidth: '300px', 
                      maxHeight: '200px', 
                      borderRadius: '8px',
                      border: '2px solid #e0e0e0'
                    }}
                  />
                </div>
              )} */}
            {/* </div> */}

            {/* Action Buttons */}
            <div
              style={{
                display: "flex",
                gap: "15px",
                justifyContent: "flex-end",
              }}
            >
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                style={{
                  padding: "12px 30px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  background: "#fff",
                  color: "#333",
                  fontSize: "16px",
                  fontWeight: "500",
                  cursor: saving ? "not-allowed" : "pointer",
                  opacity: saving ? 0.6 : 1,
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || uploadingImage}
                className="btn-primary"
                style={{
                  padding: "12px 30px",
                  minWidth: "140px",
                }}
              >
                {uploadingImage
                  ? "Uploading Image..."
                  : saving
                    ? "Creating..."
                    : "Create Service"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Custom Confirm Modal */}
      {showConfirmModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2001,
            padding: "20px",
          }}
          onClick={() => {
            setShowConfirmModal(false);
            if (confirmModalData.onCancel) confirmModalData.onCancel();
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              maxWidth: "450px",
              width: "100%",
              padding: "32px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
            }}
          >
            <div
              style={{
                marginBottom: "24px",
                fontSize: "15px",
                color: "#1f2937",
                fontFamily: "Inter, sans-serif",
                lineHeight: "1.6",
              }}
            >
              {confirmModalData.message}
            </div>

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={() => {
                  if (confirmModalData.onConfirm) confirmModalData.onConfirm();
                }}
                style={{
                  flex: 1,
                  padding: "12px 24px",
                  backgroundColor: "#2563eb",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  fontFamily: "Inter, sans-serif",
                  cursor: "pointer",
                }}
              >
                OK
              </button>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  if (confirmModalData.onCancel) confirmModalData.onCancel();
                }}
                style={{
                  flex: 1,
                  padding: "12px 24px",
                  backgroundColor: "#f3f4f6",
                  color: "#6b7280",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  fontFamily: "Inter, sans-serif",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </StaffSidebarLayout>
  );
}

export default StaffServiceAdd;
